/**
 * Locations block — a custom accessible select that swaps the active region's
 * map graphic and info card. All regions are rendered server-side; the select
 * just toggles which one is visible (no AJAX).
 *
 * Wired immediately (NOT via the lazy initSection) so the very first click on
 * the trigger is handled — initSection would defer wiring until after that click.
 */
export const locations = () => new Promise((resolve) => {
	const sections = document.querySelectorAll('[data-locations]');

	sections.forEach((section) => {
		if (section.classList.contains('js-locations')) return;
		section.classList.add('js-locations');

		const select = section.querySelector('[data-locations-select]');
		const trigger = select?.querySelector('.locations__trigger');
		const triggerText = trigger?.querySelector('.locations__trigger-text');
		const options = [...(select?.querySelectorAll('.locations__option') || [])];
		const panels = [...section.querySelectorAll('.locations__panel')];
		const maps = [...section.querySelectorAll('.locations__map')];

		if (!select || !trigger || !options.length) return;

		// Guards a programmatic trigger.focus() (after choosing) from re-opening via focusin.
		let suppressOpen = false;

		const open = () => {
			select.classList.add('open');
			trigger.setAttribute('aria-expanded', 'true');
		};
		const close = () => {
			select.classList.remove('open');
			trigger.setAttribute('aria-expanded', 'false');
		};

		const setActive = (index) => {
			const option = options[index];
			if (!option) return;

			if (triggerText) triggerText.textContent = option.querySelector('.locations__option-text')?.textContent.trim() || '';

			options.forEach((o, i) => {
				const on = i === index;
				o.classList.toggle('is-selected', on);
				o.setAttribute('aria-selected', on ? 'true' : 'false');
			});
			panels.forEach((p) => p.classList.toggle('is-active', Number(p.dataset.panel) === index));
			maps.forEach((m) => m.classList.toggle('is-active', Number(m.dataset.map) === index));
		};

		const choose = (index) => {
			// Leave the "0" state: reveal the info card, drop the overview map.
			section.classList.remove('is-empty');
			setActive(index);
			suppressOpen = true;
			trigger.focus();
			suppressOpen = false;
			close();
		};

		// Mouse: toggle (capture pre-focus state so the post-focusin click doesn't re-close).
		let wasOpen = false;
		trigger.addEventListener('mousedown', () => { wasOpen = select.classList.contains('open'); });
		trigger.addEventListener('click', (e) => {
			e.stopPropagation();
			if (wasOpen) close(); else open();
		});

		trigger.addEventListener('keydown', (e) => {
			if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
				e.preventDefault();
				open();
				options[0]?.focus();
			} else if (e.key === 'Escape') {
				close();
			}
		});

		// Open on focus-in (Tab), close when focus leaves the whole control.
		select.addEventListener('focusin', () => { if (!suppressOpen) open(); });
		select.addEventListener('focusout', (e) => {
			if (!select.contains(e.relatedTarget)) close();
		});

		options.forEach((option, i) => {
			option.addEventListener('click', () => choose(i));
			option.addEventListener('keydown', (e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					choose(i);
				} else if (e.key === 'ArrowDown') {
					e.preventDefault();
					options[Math.min(i + 1, options.length - 1)]?.focus();
				} else if (e.key === 'ArrowUp') {
					e.preventDefault();
					options[Math.max(i - 1, 0)]?.focus();
				} else if (e.key === 'Escape') {
					suppressOpen = true;
					trigger.focus();
					suppressOpen = false;
					close();
				}
			});
		});

		// Click outside closes.
		document.addEventListener('click', (e) => {
			if (!select.contains(e.target)) close();
		});
	});

	resolve(true);
});
